import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import styled from 'styled-components';
import { usePreloading } from '../../contexts/SiteContext';

const Caret = styled.span`
  color: ${props => props.$color || 'inherit'};
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  animation: ${props => props.$isVisible ? 'slowBlink 1.5s ease-in-out infinite' : 'none'};
  margin-left: 1px;
  display: inline-block;
  min-width: 1ch; /* Reserve space for the caret character */
  opacity: ${props => props.$isVisible ? '1' : '0'}; /* Hide but keep space when not visible */
  
  &::after {
    content: '_';
  }
  
  @keyframes slowBlink {
    0%, 40% {
      opacity: 1;
    }
    50%, 90% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const TypewriterText = ({ 
  children, 
  speed = 50, 
  delay = 0,
  showCaret = true,
  caretColor,
  onComplete,
  as: Component = 'span',
  ...props 
}) => {
  const [visibleText, setVisibleText] = useState('');
  const [showCaretState, setShowCaretState] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const timeoutRef = useRef(null);
  const currentCharRef = useRef(0);
  const { isPreloading } = usePreloading();

  // Store the typing function in a ref to avoid closure issues
  const typeNextCharRef = useRef();
  
  // Memoize the typing function to prevent recreation
  const typeNextChar = useCallback(() => {
    const text = children;
    const currentChar = currentCharRef.current;
    
    if (currentChar <= text.length) {
      setVisibleText(text.substring(0, currentChar));
      setShowCaretState(currentChar < text.length);
      currentCharRef.current++;
      
      if (currentCharRef.current <= text.length) {
        timeoutRef.current = setTimeout(typeNextCharRef.current, speed);
      } else {
        // Typing complete - hide caret
        setShowCaretState(false);
        setIsComplete(true);
        if (onComplete) {
          setTimeout(onComplete, 200);
        }
      }
    }
  }, [children, speed, onComplete]);

  // Update the ref whenever the function changes
  typeNextCharRef.current = typeNextChar;

  // Effect to start animation when preloader finishes
  useEffect(() => {
    if (!isPreloading && !isComplete) {
      if (!hasStarted) {
        setHasStarted(true);
        // Reset state when starting
        setVisibleText('');
        setShowCaretState(false);
        currentCharRef.current = 0;

        // Start typing after delay
        timeoutRef.current = setTimeout(() => {
          typeNextCharRef.current();
        }, delay);
      } else {
        // hasStarted is true but animation might not be running, restart it
        setVisibleText('');
        setShowCaretState(false);
        currentCharRef.current = 0;

        // Start typing after delay
        timeoutRef.current = setTimeout(() => {
          typeNextCharRef.current();
        }, delay);
      }
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isPreloading, hasStarted, isComplete, delay, children]);

  // Reset when children change - but only if we haven't started yet
  useEffect(() => {
    if (!hasStarted) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Reset all state
      setVisibleText('');
      setShowCaretState(false);
      setIsComplete(false);
      setHasStarted(false);
      currentCharRef.current = 0;
    }
  }, [children, hasStarted]);

  // Memoize styles to prevent re-creation
  const containerStyle = useMemo(() => ({ 
    position: 'relative', 
    ...props.style 
  }), [props.style]);

  const placeholderStyle = useMemo(() => ({ 
    color: 'transparent' 
  }), []);

  const overlayStyle = useMemo(() => ({ 
    position: 'absolute', 
    top: 0, 
    left: 0, 
    color: 'inherit' 
  }), []);

  return (
    <Component {...props} style={containerStyle}>
      {/* Full text as transparent placeholder to reserve space */}
      <span style={placeholderStyle}>
        {children}
        {showCaret && <Caret $color="transparent" $isVisible={false} />}
      </span>
      
      {/* Visible text overlay */}
      <span style={{...overlayStyle, pointerEvents: 'none'}}>
        {visibleText}
        {showCaret && <Caret $color={caretColor || 'inherit'} $isVisible={showCaretState} />}
      </span>
    </Component>
  );
};

// Simple typewriter component that doesn't depend on preloading context
const SimpleTypewriterText = ({ children, speed = 50, delay = 0 }) => {
  const [visibleText, setVisibleText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (currentIndex < children.length) {
      timeoutRef.current = setTimeout(() => {
        setVisibleText(children.substring(0, currentIndex + 1));
        setCurrentIndex(prev => prev + 1);
      }, currentIndex === 0 ? delay : speed);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentIndex, children, speed, delay]);

  // Reset when text changes
  useEffect(() => {
    setVisibleText('');
    setCurrentIndex(0);
  }, [children]);

  return (
    <span>
      {visibleText}
    </span>
  );
};

// Memoize TypewriterText to prevent unnecessary re-renders
const MemoizedTypewriterText = memo(TypewriterText, (prevProps, nextProps) => {
  // Only re-render if essential props change
  return (
    prevProps.children === nextProps.children &&
    prevProps.speed === nextProps.speed &&
    prevProps.delay === nextProps.delay &&
    prevProps.showCaret === nextProps.showCaret &&
    prevProps.caretColor === nextProps.caretColor &&
    prevProps.onComplete === nextProps.onComplete
  );
});

export default MemoizedTypewriterText;
export { SimpleTypewriterText };
